import { Link, router } from "@inertiajs/react"
import { CheckCircle2, RefreshCw, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import type { QualityScanStatus, QualityUrls } from "@/lib/types"

interface Props {
  kind: "clean" | "never_scanned" | "filter_zero"
  scanStatus: QualityScanStatus
  urls: QualityUrls
}

function relativeTime(iso: string | null): string {
  if (!iso) return "never"
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "unknown"
  const diff = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (diff < 60) return "just now"
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86_400)}d ago`
}

export function EmptyState({ kind, scanStatus, urls }: Props) {
  if (kind === "filter_zero") {
    return (
      <div
        className="rounded-md border border-dashed bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
        role="status"
      >
        <Search className="mx-auto mb-2 size-5" aria-hidden />
        <p>No issues match these filters.</p>
        <Button asChild variant="link" size="sm" className="mt-1 h-auto p-0">
          <Link href={urls.quality} preserveScroll>
            Clear filters
          </Link>
        </Button>
      </div>
    )
  }

  if (kind === "never_scanned") {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <Search className="size-10 text-muted-foreground" aria-hidden />
          <h2 className="text-base font-semibold">No quality scan yet for this region</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Run a scan to detect stale POIs, weak metadata, coverage gaps, and duplicates across
            the region.
          </p>
          <Button
            onClick={() => router.post(urls.run_scan, {}, { preserveScroll: true })}
            disabled={scanStatus.status === "running"}
          >
            <RefreshCw className="mr-1.5 size-4" aria-hidden />
            {scanStatus.status === "running" ? "Scanning…" : "Run first scan"}
          </Button>
        </CardContent>
      </Card>
    )
  }

  // kind === "clean"
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <CheckCircle2
          className="size-10 text-emerald-600 dark:text-emerald-400"
          aria-hidden
        />
        <h2 className="text-base font-semibold">Region is clean</h2>
        <p className="text-sm text-muted-foreground">
          Last scan: {relativeTime(scanStatus.last_completed_at)}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => router.post(urls.run_scan, {}, { preserveScroll: true })}
          disabled={scanStatus.status === "running"}
        >
          <RefreshCw className="mr-1.5 size-4" aria-hidden />
          {scanStatus.status === "running" ? "Scanning…" : "Run scan now"}
        </Button>
      </CardContent>
    </Card>
  )
}
